import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from './AppIcon';

const LIGHT_INK = '#111111';
const DARK_INK = '#f8fafc';

const getInitialDarkMode = () =>
  typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

/**
 * WhiteboardCanvas — a fully featured custom HTML5 canvas whiteboard.
 * Drop-in replacement for Tldraw (no license required).
 *
 * Props:
 *   snapshot          — serialized elements array (JSON string or array) for sync
 *   onSnapshotChange  — called with current elements[] whenever the board changes
 *   onPointerMove     — optional mouse-move callback (e.g. for remote pointer sharing)
 *   roomId            — optional room identifier (unused internally, kept for API compat)
 *   participants      — optional array of remote participants for pointer overlay
 *   pointer           — optional remote pointer {x, y, userId}
 */
const WhiteboardCanvas = ({
  snapshot,
  onSnapshotChange,
  onPointerMove,
  roomId,
  participants,
  pointer,
}) => {
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const imgCacheRef = useRef({});
  const elementsRef = useRef([]);

  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState('pencil'); // 'pencil'|'colorPencil'|'highlighter'|'eraser'|'rect'|'triangle'|'circle'
  const [isDarkMode, setIsDarkMode] = useState(getInitialDarkMode);
  const [color, setColor] = useState(() => (getInitialDarkMode() ? DARK_INK : LIGHT_INK));
  const [lineWidth, setLineWidth] = useState(3);
  const [elements, setElements] = useState([]);
  const [undoStack, setUndoStack] = useState([]);
  const [shapeStart, setShapeStart] = useState(null);
  const [shapePreview, setShapePreview] = useState(null);

  const inkColor = isDarkMode ? DARK_INK : LIGHT_INK;
  const canvasBackground = isDarkMode ? '#0d1017' : '#ffffff';
  const gridColor = isDarkMode ? 'rgba(148, 163, 184, 0.075)' : 'rgba(15, 23, 42, 0.055)';
  const colors = [
    { value: inkColor, label: 'Ink' },
    { value: '#169873', label: 'Emerald' },
    { value: '#0ea5e9', label: 'Sky' },
    { value: '#ef4444', label: 'Red' },
    { value: '#f59e0b', label: 'Amber' },
    { value: '#8b5cf6', label: 'Violet' },
    { value: '#ec4899', label: 'Pink' },
  ];

  const highlighterColors = [
    { value: 'rgba(250, 204, 21, 0.4)', label: 'Yellow' },
    { value: 'rgba(74, 222, 128, 0.4)', label: 'Green' },
    { value: 'rgba(96, 165, 250, 0.4)', label: 'Blue' },
    { value: 'rgba(251, 146, 60, 0.4)', label: 'Orange' },
    { value: 'rgba(244, 114, 182, 0.4)', label: 'Pink' },
  ];

  const isShapeTool = tool === 'rect' || tool === 'triangle' || tool === 'circle';
  const isDrawTool = tool === 'pencil' || tool === 'colorPencil' || tool === 'highlighter';

  const setElementsSynced = useCallback((updater) => {
    setElements((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      elementsRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    const updateTheme = () => setIsDarkMode(getInitialDarkMode());
    updateTheme();

    const observer = new MutationObserver(updateTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setColor((prev) => (prev === LIGHT_INK || prev === DARK_INK ? inkColor : prev));
  }, [inkColor]);

  // ── Drawing helpers ──────────────────────────────────────────────────────────
  const drawStroke = (ctx, el) => {
    if (!el.points || el.points.length < 2) return;
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (el.tool === 'highlighter') {
      ctx.globalAlpha = 1;
      ctx.strokeStyle = el.color;
      ctx.lineWidth = el.width * 6;
      ctx.globalCompositeOperation = 'source-over';
    } else if (el.tool === 'colorPencil') {
      ctx.strokeStyle = el.color;
      ctx.lineWidth = el.width;
      ctx.globalAlpha = 0.65;
    } else {
      ctx.strokeStyle = el.color;
      ctx.lineWidth = el.width;
    }

    ctx.beginPath();
    ctx.moveTo(el.points[0].x, el.points[0].y);
    for (let i = 1; i < el.points.length; i++) ctx.lineTo(el.points[i].x, el.points[i].y);
    ctx.stroke();

    // Color pencil texture: extra jittered thin parallel strokes
    if (el.tool === 'colorPencil' && el.points.length > 2) {
      ctx.globalAlpha = 0.25;
      ctx.lineWidth = Math.max(1, el.width * 0.4);
      for (let offset = -1; offset <= 1; offset += 2) {
        ctx.beginPath();
        ctx.moveTo(el.points[0].x + offset, el.points[0].y + offset);
        for (let i = 1; i < el.points.length; i++) {
          const jx = el.points[i].x + offset + (Math.random() - 0.5) * 1.2;
          const jy = el.points[i].y + offset + (Math.random() - 0.5) * 1.2;
          ctx.lineTo(jx, jy);
        }
        ctx.stroke();
      }
    }
    ctx.restore();
  };

  const drawShape = (ctx, el) => {
    ctx.save();
    ctx.strokeStyle = el.color;
    ctx.lineWidth = el.width;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const { start, end, shape } = el;
    const x = Math.min(start.x, end.x);
    const y = Math.min(start.y, end.y);
    const w = Math.abs(end.x - start.x);
    const h = Math.abs(end.y - start.y);
    ctx.beginPath();
    if (shape === 'rect') {
      ctx.rect(x, y, w, h);
    } else if (shape === 'circle') {
      ctx.ellipse((start.x + end.x) / 2, (start.y + end.y) / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
    } else if (shape === 'triangle') {
      const mx = (start.x + end.x) / 2;
      ctx.moveTo(mx, y);
      ctx.lineTo(x, y + h);
      ctx.lineTo(x + w, y + h);
      ctx.closePath();
    }
    ctx.stroke();
    ctx.restore();
  };

  const drawImage = (ctx, el) => {
    const img = imgCacheRef.current[el.src];
    if (img) ctx.drawImage(img, el.x, el.y, el.w, el.h);
  };

  const loadImage = (src) => {
    if (imgCacheRef.current[src]) return Promise.resolve(imgCacheRef.current[src]);
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => { imgCacheRef.current[src] = img; resolve(img); };
      img.onerror = () => resolve(null);
      img.src = src;
    });
  };

  const renderBoard = useCallback((ctx, width, height, boardElements) => {
    ctx.save();
    ctx.fillStyle = canvasBackground;
    ctx.fillRect(0, 0, width, height);
    ctx.restore();

    ctx.save();
    ctx.strokeStyle = gridColor;
    ctx.lineWidth = 1;
    const gs = 25;
    for (let gx = 0; gx < width; gx += gs) {
      ctx.beginPath();
      ctx.moveTo(gx, 0);
      ctx.lineTo(gx, height);
      ctx.stroke();
    }
    for (let gy = 0; gy < height; gy += gs) {
      ctx.beginPath();
      ctx.moveTo(0, gy);
      ctx.lineTo(width, gy);
      ctx.stroke();
    }
    ctx.restore();

    boardElements.forEach((el) => {
      if (el.type === 'stroke') drawStroke(ctx, el);
      else if (el.type === 'shape') drawShape(ctx, el);
      else if (el.type === 'image') drawImage(ctx, el);
    });
  }, [canvasBackground, gridColor]);

  const exportBoardImage = useCallback((boardElements) => {
    const canvas = canvasRef.current;
    if (!canvas) return '';

    const rect = canvas.getBoundingClientRect();
    const sourceWidth = Math.max(1, rect.width || canvas.width || 1);
    const sourceHeight = Math.max(1, rect.height || canvas.height || 1);
    const maxWidth = 1280;
    const scale = Math.min(1, maxWidth / sourceWidth);
    const outputWidth = Math.max(1, Math.round(sourceWidth * scale));
    const outputHeight = Math.max(1, Math.round(sourceHeight * scale));

    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = outputWidth;
    exportCanvas.height = outputHeight;
    const ctx = exportCanvas.getContext('2d');
    if (!ctx) return '';

    ctx.save();
    ctx.scale(scale, scale);
    renderBoard(ctx, sourceWidth, sourceHeight, boardElements || []);
    ctx.restore();

    try {
      return exportCanvas.toDataURL('image/jpeg', 0.78);
    } catch (err) {
      console.warn('WhiteboardCanvas: failed to export board image', err);
      return '';
    }
  }, [renderBoard]);

  const notifySnapshotChange = useCallback((boardElements) => {
    if (!onSnapshotChange) return;
    onSnapshotChange(boardElements, {
      imageDataUrl: exportBoardImage(boardElements),
      imageMimeType: 'image/jpeg',
    });
  }, [exportBoardImage, onSnapshotChange]);

  // ── Redraw ───────────────────────────────────────────────────────────────────
  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const dpr = window.devicePixelRatio || 1;
    const lw = canvas.width / dpr, lh = canvas.height / dpr;

    // Solid background keeps the board legible when the app theme changes.
    ctx.save();
    ctx.fillStyle = canvasBackground;
    ctx.fillRect(0, 0, lw, lh);
    ctx.restore();

    renderBoard(ctx, lw, lh, elements);

    if (shapePreview) drawShape(ctx, shapePreview);
  }, [elements, shapePreview, canvasBackground, gridColor, renderBoard]);

  // Sync snapshot from outside
  useEffect(() => {
    if (!snapshot) return;
    try {
      const decoded = typeof snapshot === 'string' ? JSON.parse(snapshot) : snapshot;
      if (Array.isArray(decoded)) {
        decoded.filter(e => e.type === 'image').forEach(e => loadImage(e.src));
        elementsRef.current = decoded;
        setElementsSynced(decoded);
      }
    } catch (e) {
      console.error('WhiteboardCanvas: failed to parse snapshot', e);
    }
  }, [setElementsSynced, snapshot]);

  useEffect(() => { redrawCanvas(); }, [redrawCanvas]);

  // Handle HiDPI resize
  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.parentElement.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      canvas.getContext('2d').scale(dpr, dpr);
      redrawCanvas();
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [redrawCanvas]);

  // ── Coordinate helper ────────────────────────────────────────────────────────
  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  // ── Eraser hit-test ──────────────────────────────────────────────────────────
  const distToSegment = (p, a, b) => {
    const dx = b.x - a.x, dy = b.y - a.y;
    const lenSq = dx * dx + dy * dy;
    if (lenSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);
    let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
  };

  const eraseAt = (coords) => {
    const eraserRadius = lineWidth * 3;
    setElementsSynced((prev) =>
      prev.filter((el) => {
        if (el.type === 'stroke' && el.points) {
          for (let i = 0; i < el.points.length - 1; i++) {
            if (distToSegment(coords, el.points[i], el.points[i + 1]) < eraserRadius + (el.width || 2)) return false;
          }
        }
        if (el.type === 'shape') {
          const { start, end } = el;
          const x1 = Math.min(start.x, end.x) - 6, y1 = Math.min(start.y, end.y) - 6;
          const x2 = Math.max(start.x, end.x) + 6, y2 = Math.max(start.y, end.y) + 6;
          if (coords.x >= x1 && coords.x <= x2 && coords.y >= y1 && coords.y <= y2) return false;
        }
        if (el.type === 'image') {
          if (coords.x >= el.x && coords.x <= el.x + el.w && coords.y >= el.y && coords.y <= el.y + el.h) return false;
        }
        return true;
      })
    );
  };

  // ── Undo / Redo ──────────────────────────────────────────────────────────────
  const handleUndo = () => {
    setElementsSynced((prev) => {
      if (!prev.length) return prev;
      const last = prev[prev.length - 1];
      setUndoStack((us) => [...us, last]);
      const next = prev.slice(0, -1);
      notifySnapshotChange(next);
      return next;
    });
  };

  const handleRedo = () => {
    setUndoStack((prev) => {
      if (!prev.length) return prev;
      const last = prev[prev.length - 1];
      setElementsSynced((els) => {
        const next = [...els, last];
        notifySnapshotChange(next);
        return next;
      });
      return prev.slice(0, -1);
    });
  };

  // ── Mouse / Touch events ─────────────────────────────────────────────────────
  const handleMouseDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    const coords = getCanvasCoords(e);

    if (tool === 'eraser') { setIsDrawing(true); eraseAt(coords); return; }
    if (isShapeTool) { setShapeStart(coords); setIsDrawing(true); return; }

    setIsDrawing(true);
    setUndoStack([]);
    const newStroke = {
      type: 'stroke',
      points: [coords],
      tool,
      color: tool === 'highlighter'
        ? (highlighterColors.find(c => c.value === color) ? color : highlighterColors[0].value)
        : color,
      width: lineWidth,
    };
    setElementsSynced((prev) => [...prev, newStroke]);
  };

  const handleMouseMove = (e) => {
    if (onPointerMove) onPointerMove(e);
    if (!isDrawing) return;
    const coords = getCanvasCoords(e);

    if (tool === 'eraser') { eraseAt(coords); return; }
    if (isShapeTool && shapeStart) {
      setShapePreview({ type: 'shape', shape: tool, start: shapeStart, end: coords, color, width: lineWidth });
      return;
    }

    setElementsSynced((prev) => {
      if (!prev.length) return prev;
      const lastEl = { ...prev[prev.length - 1] };
      if (lastEl.type !== 'stroke') return prev;
      lastEl.points = [...lastEl.points, coords];
      return [...prev.slice(0, -1), lastEl];
    });
  };

  const handleMouseUp = () => {
    if (!isDrawing) return;
    setIsDrawing(false);

    let nextSnapshot = elementsRef.current;

    if (isShapeTool && shapeStart && shapePreview) {
      const { start, end } = shapePreview;
      if (Math.hypot(end.x - start.x, end.y - start.y) > 5) {
        nextSnapshot = [...elementsRef.current, { ...shapePreview }];
        setElementsSynced(nextSnapshot);
      }
      setShapeStart(null);
      setShapePreview(null);
    }

    notifySnapshotChange(nextSnapshot);
  };

  const handleClear = () => {
    setElementsSynced([]);
    setUndoStack([]);
    notifySnapshotChange([]);
  };

  // ── Image upload ─────────────────────────────────────────────────────────────
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const src = ev.target.result;
      const img = new Image();
      img.onload = () => {
        imgCacheRef.current[src] = img;
        const maxW = 320, maxH = 260;
        let w = img.naturalWidth, h = img.naturalHeight;
        if (w > maxW) { h = h * (maxW / w); w = maxW; }
        if (h > maxH) { w = w * (maxH / h); h = maxH; }
        const newEl = { type: 'image', src, x: 40, y: 80, w, h };
        setElementsSynced((prev) => {
          const next = [...prev, newEl];
          notifySnapshotChange(next);
          return next;
        });
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // ── Keyboard shortcuts ───────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) handleRedo(); else handleUndo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── Toolbar helpers ──────────────────────────────────────────────────────────
  const activeColors = tool === 'highlighter' ? highlighterColors : colors;
  const showColors = isDrawTool || isShapeTool;

  const ToolBtn = ({ id, icon, label, compact }) => (
    <button
      type="button"
      onClick={() => {
        setTool(id);
        if (id === 'highlighter' && !highlighterColors.find(c => c.value === color)) setColor(highlighterColors[0].value);
        else if (id !== 'highlighter' && highlighterColors.find(c => c.value === color)) setColor(colors[0].value);
      }}
      title={label}
      className={`flex h-8 items-center gap-1 rounded-xl px-2.5 text-[11px] transition-all border whitespace-nowrap ${
        tool === id
          ? 'bg-primary text-primary-foreground border-transparent shadow-sm'
          : 'border-slate-200 text-slate-700 hover:bg-slate-100 dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/[0.08]'
      }`}
    >
      <Icon name={icon} size={13} />
      {!compact && label}
    </button>
  );

  return (
    <div
      className="relative h-full w-full overflow-hidden bg-white dark:bg-[#0d1017] flex flex-col"
      onMouseMove={onPointerMove}
    >
      {/* ── Floating Toolbar ─────────────────────────────────────────────── */}
      <div className="absolute top-3 left-3 right-3 z-20 flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white/95 px-3 py-1.5 shadow-lg backdrop-blur-md dark:border-white/10 dark:bg-[#17181e]/95">
        {/* Drawing tools */}
        <div className="flex items-center gap-1">
          <ToolBtn id="pencil" icon="Pencil" label="Pencil" />
          <ToolBtn id="colorPencil" icon="PenTool" label="Color Pencil" compact />
          <ToolBtn id="highlighter" icon="Highlighter" label="Highlight" compact />
          <ToolBtn id="eraser" icon="Eraser" label="Eraser" />
        </div>

        <div className="h-5 w-px bg-slate-200 dark:bg-white/10" />

        {/* Shape tools */}
        <div className="flex items-center gap-1">
          <ToolBtn id="rect" icon="Square" label="Rect" compact />
          <ToolBtn id="circle" icon="Circle" label="Circle" compact />
          <ToolBtn id="triangle" icon="Triangle" label="Tri" compact />
        </div>

        <div className="h-5 w-px bg-slate-200 dark:bg-white/10" />

        {/* Color palette */}
        {showColors && (
          <div className="flex items-center gap-1">
            {activeColors.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => setColor(c.value)}
                style={{ backgroundColor: c.value }}
                title={c.label}
                className={`h-5 w-5 rounded-full border transition-all ${
                  color === c.value ? 'ring-2 ring-primary ring-offset-1 ring-offset-white scale-110 dark:ring-offset-[#17181e]' : 'border-black/10 hover:scale-105 dark:border-white/20'
                }`}
              />
            ))}
          </div>
        )}

        {/* Size slider */}
        <div className="flex items-center gap-1.5 ml-auto">
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium select-none dark:text-slate-300">Size</span>
          <input
            type="range"
            min={1}
            max={tool === 'highlighter' ? 12 : 20}
            value={lineWidth}
            onChange={(e) => setLineWidth(Number(e.target.value))}
            className="w-20 h-1.5 cursor-pointer"
            style={{ accentColor: 'var(--color-primary, #10e58f)' }}
          />
          <span className="text-[10px] tabular-nums font-mono text-slate-400 w-4 text-center dark:text-slate-300">{lineWidth}</span>
        </div>

        <div className="h-5 w-px bg-slate-200 dark:bg-white/10" />

        {/* Actions */}
        <div className="flex items-center gap-1">
          <button type="button" onClick={handleUndo} title="Undo (Ctrl+Z)"
            className="flex h-7 w-7 items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 transition-colors dark:text-slate-300 dark:hover:bg-white/[0.08]">
            <Icon name="Undo2" size={14} />
          </button>
          <button type="button" onClick={handleRedo} title="Redo (Ctrl+Shift+Z)"
            className="flex h-7 w-7 items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 transition-colors dark:text-slate-300 dark:hover:bg-white/[0.08]">
            <Icon name="Redo2" size={14} />
          </button>

          <div className="h-4 w-px bg-slate-200 dark:bg-white/10" />

          <button type="button" onClick={() => fileInputRef.current?.click()} title="Upload image"
            className="flex h-7 items-center gap-1 rounded-lg px-2 text-[11px] hover:bg-slate-100 text-slate-500 transition-colors dark:text-slate-300 dark:hover:bg-white/[0.08]">
            <Icon name="ImagePlus" size={14} />
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />

          <div className="h-4 w-px bg-slate-200 dark:bg-white/10" />

          <button type="button" onClick={handleClear} title="Clear board"
            className="flex h-7 items-center gap-1 rounded-lg px-2 text-[11px] hover:bg-red-50 text-red-500 transition-colors dark:text-red-400 dark:hover:bg-red-500/10">
            <Icon name="Trash2" size={13} />
          </button>
        </div>
      </div>

      {/* ── Canvas area ─────────────────────────────────────────────────────── */}
      <div className={`flex-1 w-full relative bg-white dark:bg-[#0d1017] min-h-0 ${
        tool === 'eraser' ? 'cursor-cell' : 'cursor-crosshair'
      }`}>
        <canvas
          ref={canvasRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleMouseDown}
          onTouchMove={handleMouseMove}
          onTouchEnd={handleMouseUp}
          className="absolute inset-0 block w-full h-full touch-none"
        />
      </div>
    </div>
  );
};

export default WhiteboardCanvas;
